import api from '@/lib/axiosInstance'
import type { IntakeDraft, IntakeFields, LotLookup } from './aiIntake'

/**
 * Finding the land on JUPEM MyLot from this browser.
 *
 * MyLot only answers computers inside Malaysia, and the API may be hosted
 * outside it. When the API could not ask, it marks the draft's lookup with
 * `browser`, and the review asks from here instead: the API says which
 * queries to send, this fetches them from MyLot, and the API works out the
 * answer. Nothing about MyLot is decided here.
 */

/** A lookup the API left for the browser to do. */
export const awaitingLot = (draft: IntakeDraft) => draft.lotLookup?.status === 'unavailable' && draft.lotLookup.browser === true

const SENT = ['state', 'district', 'mukim', 'lotNo', 'neighbourLots', 'landArea', 'street', 'township', 'postcode', 'city', 'rawAddress', 'jupemNo', 'lat', 'lng']
const MAX_ROUNDS = 6
const FETCH_TIMEOUT_MS = 20000

/** MyLot did not answer this computer, as against the API failing. */
export class MyLotUnreachable extends Error {}

/** Runs the lookup. Gives the finished lookup (null when there was nothing to look up) and the fields it settled. */
export async function lookUpLot(fields: IntakeFields): Promise<{ lotLookup: LotLookup | null; fields: IntakeFields }> {
    const sent = Object.fromEntries(SENT.map((k) => [k, fields[k] ?? null]))
    const answers: Record<string, unknown> = {}
    for (let round = 0; round < MAX_ROUNDS; round++) {
        const { data } = await api.post('/listings/ai/lot-lookup', { fields: sent, answers }, { headers: { 'Content-Type': 'application/json' }, timeout: 60000 })
        if (!data.status) throw new Error(data.error || 'The lot could not be looked up.')
        if (!Array.isArray(data.need)) return { lotLookup: data.lotLookup ?? null, fields: data.fields ?? {} }
        await Promise.all(
            data.need.map(async (ask: { key: string; url: string }) => {
                try {
                    const response = await fetch(ask.url, { headers: { token: data.token }, signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
                    answers[ask.key] = await response.json()
                } catch {
                    throw new MyLotUnreachable()
                }
            }),
        )
    }
    throw new Error('MyLot kept asking for more.')
}

/** The draft with a finished lookup laid over it. */
export function withLookup(draft: IntakeDraft, lotLookup: LotLookup | null, fields: IntakeFields): IntakeDraft {
    const filled = Object.keys(fields)
    return {
        ...draft,
        lotLookup: lotLookup ?? undefined,
        fields: { ...draft.fields, ...fields },
        missingFields: draft.missingFields.filter((k) => !filled.includes(k)),
    }
}

/** The draft with a lookup that could not be done from here, so it is not tried again. */
export function withoutLookup(draft: IntakeDraft, error: unknown): IntakeDraft {
    const abroad = error instanceof MyLotUnreachable
    return {
        ...draft,
        lotLookup: {
            ...(draft.lotLookup as LotLookup),
            browser: false,
            reason: abroad ? 'MyLot did not answer this computer. It only answers from inside Malaysia: check the lot by hand.' : 'MyLot could not be checked. Check the lot by hand.',
            detail: abroad ? 'MyLot refused the connection from this browser.' : String((error as Error)?.message ?? error).slice(0, 300),
        },
    }
}
